import {computed, ref} from 'vue';
import {
  type LatLngBounds,
  type LatLngExpression,
  type Layer,
  LayerGroup,
  type Map as LeafletMap,
  Marker,
} from 'leaflet';

import {settings} from '@/config/settings';

import type {TId} from '@/assets/core/assets/api/types';

import {lFeatureLoader} from '@/assets/leaflet/assets/hooks/LFeatureLoaderHook';

import {parsePolygonFromBounds} from '@/assets/leaflet/assets/helpers/parse';
import {getDefaultTilePresetIndex} from '@/assets/leaflet/assets/helpers/tile_layers';
import {featureCache} from '@/services/cache/feature';
import {resolveMainFeatureAcrossLayers} from '@/services/helpers/linkedFeatures';
import type {Feature as ITurfFeature} from '@turf/turf';
import {pointOnFeature} from '@turf/turf';

export interface ILMapStateZoomDefaults {
  default: number;
  min: number;
  max: number;
}

export type ILMapState = ReturnType<typeof useLMapStateHook>;
export type TLMapActions = 'open_popup';

const parseMapCenter = (): LatLngExpression => {
  const centerStr = settings.SERVICES.MAP.INITIAL_CENTER;
  const [lat, lng] = centerStr.split(',').map((v) => parseFloat(v.trim()));
  return [lat, lng];
};

const parseMapZoom = (): number => {
  return parseInt(settings.SERVICES.MAP.INITIAL_ZOOM, 10);
};

const zoomDefaults: ILMapStateZoomDefaults = {
  default: parseMapZoom(),
  min: 3,
  max: 19,
};

const defaultCenter: LatLngExpression = parseMapCenter();

export const useLMapActionsHook = () => {
  const delimiter = '|';
  const actionsMap = new Map<string, unknown>();
  const featuresMap = new Map<string, Layer | LayerGroup>();

  const add = (
    key: {
      action: TLMapActions;
      layerId?: TId;
      featureId?: TId;
    },
    value?: unknown,
  ) => {
    const keyStr = [key.action, key.layerId, key.featureId]
      .filter((item) => !!item)
      .join(delimiter);
    actionsMap.set(keyStr, value);
  };

  const pop = (key: {action: TLMapActions; layerId?: TId; featureId?: TId}) => {
    const keyStr = [key.action, key.layerId, key.featureId]
      .filter((item) => !!item)
      .join(delimiter);

    const val = actionsMap.get(keyStr);
    if (val) actionsMap.delete(keyStr);
    return val;
  };

  const addFeature = (layerId: TId, featureId: TId, layer: Layer) => {
    const keyStr = [layerId, featureId]
      .filter((item) => !!item)
      .join(delimiter);
    featuresMap.set(keyStr, layer);
  };

  const getFeature = (layerId: TId, featureId: TId) => {
    const keyStr = [layerId, featureId]
      .filter((item) => !!item)
      .join(delimiter);
    return featuresMap.get(keyStr);
  };

  const deleteFeature = (layerId: TId, featureId: TId) => {
    const keyStr = [layerId, featureId]
      .filter((item) => !!item)
      .join(delimiter);
    featuresMap.delete(keyStr);
  };

  const openPopup = (layerId: TId, featureId: TId) => {
    const keyStr = [layerId, featureId]
      .filter((item) => !!item)
      .join(delimiter);

    const layer = featuresMap.get(keyStr);

    const isLayerGroup = layer instanceof LayerGroup;
    const subLayersCount = isLayerGroup
      ? (layer as LayerGroup).getLayers().length
      : -1;

    console.log('[atlas:openPopup]', {
      layerId,
      featureId,
      hasLayer: !!layer,
      isLayerGroup,
      subLayersCount,
    });

    if (isLayerGroup) {
      const subLayers = (layer as LayerGroup).getLayers();
      if (subLayers[0] instanceof Marker) {
        console.log('[atlas:openPopup] → LayerGroup+Marker');
        (layer as LayerGroup).openPopup(subLayers[0].getLatLng());
      } else {
        console.log('[atlas:openPopup] → queue (empty LayerGroup)');
        add({action: 'open_popup', layerId, featureId}, true);
      }
    } else if (layer) {
      console.log('[atlas:openPopup] → layer.openPopup()');
      layer.openPopup();
    } else {
      console.log('[atlas:openPopup] → queue (not in featuresMap)');
      add({action: 'open_popup', layerId, featureId}, true);
    }
  };

  return {
    addFeature,
    getFeature,
    deleteFeature,
    add,
    pop,
    openPopup,
  };
};

export const useLMapStateHook = (baseTarget?: string) => {
  const actions = useLMapActionsHook();

  const target = ref<string>(baseTarget ?? 'map');
  const map = ref<LeafletMap | undefined>(undefined);

  const center = ref<LatLngExpression>(defaultCenter);
  const zoom = ref<number>(zoomDefaults.default);
  const bounds = ref<LatLngBounds | undefined>(undefined);

  const layersIdTile = ref<TId[]>([]);
  const layersIdFeature = ref<TId[]>([]);

  const tilePresetIndex = ref<number>(getDefaultTilePresetIndex());

  const diagnosticId = ref<TId | undefined>(undefined);

  const actionsClearLayers = ref<Array<(id: TId) => void>>([]);

  const getTarget = computed(() => target.value);
  const getLayersIdTile = computed(() => layersIdTile.value);
  const getLayersIdFeature = computed(() => layersIdFeature.value);

  const getDiagnosticId = computed(() => diagnosticId.value);
  const getTilePresetIndex = computed(() => tilePresetIndex.value);

  const getFeaturesCache = computed(() => featureCache.getCache.value);

  const getBoundsPolygon = computed(() => parsePolygonFromBounds(bounds.value));

  const setLayersIdTile = (items?: TId[]) => (layersIdTile.value = items ?? []);
  const setLayersIdFeature = (items?: TId[]) =>
    (layersIdFeature.value = items ?? []);
  const setDiagnosticId = (item?: TId) => (diagnosticId.value = item);
  const setTilePresetIndex = (index: number) => (tilePresetIndex.value = index);

  const setView = (c?: LatLngExpression, z?: number) => {
    if (map.value) {
      (map.value as LeafletMap).setView(
        c ?? defaultCenter,
        z ?? zoomDefaults.default,
      );
    } else {
      if (c) center.value = c;
      if (z) zoom.value = z;
    }
  };

  const targetFeatureFromSearch = async (
    layerId: TId,
    feature: ITurfFeature,
  ) => {
    if (!feature.geometry) return;

    const props = feature.properties as Record<string, unknown>;
    const ownId = props?.__id__ as string;
    const mainId = props?.main_id as string | undefined;
    const isLinked = !!mainId && mainId !== ownId;

    console.log('[atlas:targetFeatureFromSearch] start', {
      layerId,
      ownId,
      mainId,
      isLinked,
    });

    const resolved = isLinked
      ? resolveMainFeatureAcrossLayers(
          getFeaturesCache.value,
          mainId!,
          feature,
          layerId,
        )
      : {feature, layerId};

    const mainInCache = resolved.feature !== feature;

    console.log('[atlas:targetFeatureFromSearch] resolved', {
      mainInCache,
      resolvedFeatureId: (
        resolved.feature.properties as Record<string, unknown>
      )?.__id__,
    });

    const coordinates = (
      pointOnFeature(mainInCache ? resolved.feature : feature).geometry
        ?.coordinates ?? [0, 0]
    ).reverse() as [number, number];

    const openLayerId = isLinked ? layerId : resolved.layerId;
    const openFeatureId = isLinked ? mainId! : ownId;

    const isInViewport = bounds.value?.contains(coordinates) ?? false;

    console.log('[atlas:targetFeatureFromSearch] nav', {
      openLayerId,
      openFeatureId,
      coordinates,
      isInViewport,
    });

    if (isInViewport) {
      console.log('[atlas:targetFeatureFromSearch] → openPopup');
      actions.openPopup(openLayerId, openFeatureId);
    } else {
      console.log('[atlas:targetFeatureFromSearch] → move + openPopup');
      center.value = coordinates;
      (map.value as LeafletMap).once('moveend', () => {
        console.log('[atlas:targetFeatureFromSearch] moveend → openPopup');
        actions.openPopup(openLayerId, openFeatureId);
      });
    }
  };

  return {
    actions,

    loader: lFeatureLoader,
    zoomDefaults,
    defaultCenter,

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    map,

    target,
    center,
    zoom,
    bounds,
    getBoundsPolygon,

    layersIdTile,
    layersIdFeature,
    tilePresetIndex,

    getTarget,
    getLayersIdTile,
    setLayersIdTile,
    getLayersIdFeature,
    setLayersIdFeature,

    getDiagnosticId,
    setDiagnosticId,

    getTilePresetIndex,
    setTilePresetIndex,

    getFeaturesCache,

    actionsClearLayers,

    setView,
    targetFeatureFromSearch,
  };
};
